import React, { useEffect, useMemo, useRef, useState } from "react";
import { Timeline } from "@xzdarcy/react-timeline-editor";
import { Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { Add, Remove, ChevronLeft, ChevronRight, Search } from "@mui/icons-material";
import { SSENSE_URL } from "../../config/stepoutAPI";
import axios from "axios";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { CustomActionRender } from "./CustomRender";
import { attribTitle, ATTRIBUTE_LABEL } from "../../common/abbr";

// ─── Visual config ───
const ROW_HEIGHT = 30;
const SCALE_HEIGHT = 32; // @xzdarcy time-area header height
const GUTTER_W = 132;
const MAX_ACTIONS = 3000;
const EMPTY_TOKEN = "X-X-X-X-X-X-X-X-X-X-X-X";

const TEAM_COLOR = { A: "#1C64F2", B: "#F97316" };
const ATTRIBUTE_COLOR = {
  passing: "#3B82F6", shooting: "#EF4444", dribbling: "#A855F7", ball_control: "#8B5CF6",
  pace: "#06B6D4", physical: "#F59E0B", defending: "#10B981", goalkeeping: "#EC4899",
  special_actions: "#6B7280", x_passing: "#60A5FA", x_defending: "#34D399",
  x_physical: "#FBBF24", x_dribbling: "#C084FC", x_goalkeeping: "#F472B6",
  x_special_actions: "#9CA3AF",
};
// Order categories appear in the lanes.
const CATEGORY_ORDER = [
  "passing", "shooting", "dribbling", "ball_control", "pace", "physical",
  "defending", "goalkeeping", "special_actions",
  "x_passing", "x_defending", "x_physical", "x_dribbling", "x_goalkeeping", "x_special_actions",
];

const pad = (n) => String(n).padStart(2, "0");
const fmtTime = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
};

const parseTimestamp = (timestamp) => {
  if (!timestamp || typeof timestamp !== "string" || !timestamp.includes(":")) return 0;
  const [hh, mm, ssMs] = timestamp.split(":");
  if (!ssMs) return 0;
  const [ss, ms = "0"] = ssMs.split(".");
  return (
    parseInt(hh || "0") * 3600 * 1000 +
    parseInt(mm || "0") * 60 * 1000 +
    parseInt(ss || "0") * 1000 +
    parseInt(ms || "0")
  );
};

// Parse the full match string into enriched events (start/end + metadata).
const enrichEvents = (rawString) => {
  const entries = String(rawString || "").split(",").map((s) => s.trim()).filter(Boolean).filter((t) => t !== EMPTY_TOKEN);
  const events = [];
  let i = 0;
  while (i < entries.length) {
    const current = entries[i]?.split("-");
    const next = entries[i + 1]?.split("-");
    if (!current || current.length < 7) { i += 1; continue; }

    const code = current[2] || "";
    const meta = attribTitle[code];
    const nextCode = next?.[2] || "";
    const currTime = parseTimestamp(current[6]);

    let endTime, isPaired = false;
    if (next && next.length >= 7 && nextCode.startsWith("X") && nextCode.substring(1) === code) {
      endTime = parseTimestamp(next[6]);
      isPaired = true;
    } else {
      endTime = currTime + 1000;
    }

    if (meta) {
      const type = meta.type;
      events.push({
        id: `evt-${i}-${code}`,
        start: currTime / 1000,
        end: (currTime === endTime ? endTime + 1000 : endTime) / 1000,
        effectId: "event-marker",
        type,
        catLabel: ATTRIBUTE_LABEL[type] || type,
        eventLabel: meta.events?.[current[3]] ?? `Event ${current[3]}`,
        team: current[0],
        jersey: current[1] === "X" ? "" : current[1],
        name: "",
        color: ATTRIBUTE_COLOR[type] || "#3f51b5",
        raw: isPaired ? `${entries[i]},${entries[i + 1]}` : entries[i],
      });
    }
    i += isPaired ? 2 : 1;
  }
  return events;
};

const fetchMatchVideoString = async ({ queryKey }) => {
  const [, { match_id, analyst_id, token }] = queryKey;
  const { data } = await axios.post(
    `${SSENSE_URL}get_match_video_string/v2/`,
    { match_id, analyst_id },
    { headers: { Authorization: "ANALYST " + token } }
  );
  return data;
};

// ─── Overview density bar (whole match at a glance) ───
const OverviewBar = ({ events, durationSec, playheadSec, viewStart, viewEnd, onSeek }) => {
  const ref = useRef(null);
  const seekFromEvent = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect || !durationSec) return;
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    onSeek(ratio * durationSec);
  };
  return (
    <div
      ref={ref}
      onMouseDown={(e) => { seekFromEvent(e); const mv = (ev) => seekFromEvent(ev); const up = () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); }; window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up); }}
      style={{
        position: "relative", height: 34, width: "100%", cursor: "pointer",
        background: "var(--surface-2)", borderRadius: 6, border: "1px solid var(--border-1)", overflow: "hidden",
      }}
    >
      {/* visible-window highlight */}
      {durationSec > 0 && (
        <div style={{
          position: "absolute", top: 0, bottom: 0,
          left: `${(viewStart / durationSec) * 100}%`,
          width: `${Math.max(1, ((viewEnd - viewStart) / durationSec) * 100)}%`,
          background: "rgba(232,108,0,0.15)", borderLeft: "1px solid rgba(232,108,0,0.5)", borderRight: "1px solid rgba(232,108,0,0.5)",
        }} />
      )}
      {/* event ticks */}
      {durationSec > 0 && events.map((e) => (
        <div key={e.id} style={{
          position: "absolute", top: 4, bottom: 4, width: 2,
          left: `${(e.start / durationSec) * 100}%`,
          background: TEAM_COLOR[e.team] || "#888", opacity: 0.6,
        }} />
      ))}
      {/* playhead */}
      {durationSec > 0 && (
        <div style={{
          position: "absolute", top: 0, bottom: 0, width: 2, background: "rgba(232,108,0,1)",
          left: `${(playheadSec / durationSec) * 100}%`,
        }} />
      )}
    </div>
  );
};

const VideoTimelineEditor = ({
  durationMs, videoTimeMs, setTime, setIsPlaying, timelineState, setShowTimeline, showTimeline, matchId, analystId,
}) => {
  const durationSec = useMemo(() => durationMs / 1000, [durationMs]);
  const videoTimeSec = useMemo(() => videoTimeMs / 1000, [videoTimeMs]);
  const containerRef = useRef(null);
  const autoScrollWhenPlay = useRef(true);

  const [timelineHeight, setTimelineHeight] = useState(220);
  const resizingRef = useRef(false);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [zoomLevel, setZoomLevel] = useState(5);

  // Filters
  const [teams, setTeams] = useState({ A: true, B: true });
  const [disabledCats, setDisabledCats] = useState(() => new Set());
  const [jerseyQuery, setJerseyQuery] = useState("");

  const { token } = useSelector((state) => ({ token: state.auth.token }));

  const { data } = useQuery(
    ["matchVideoString", { match_id: matchId, analyst_id: analystId, token }],
    fetchMatchVideoString,
    { enabled: !!matchId && !!analystId && !!token }
  );

  // Build all events ONCE per string (no dependency on playhead → no per-tick rebuild).
  const allEvents = useMemo(() => {
    const rawString = typeof data === "string" ? data : data?.temp_string;
    return enrichEvents(rawString);
  }, [data]);

  // Categories actually present, in canonical order.
  const presentCats = useMemo(() => {
    const set = new Set(allEvents.map((e) => e.type));
    return CATEGORY_ORDER.filter((c) => set.has(c));
  }, [allEvents]);

  const filtered = useMemo(() => {
    const q = jerseyQuery.trim();
    return allEvents.filter(
      (e) => teams[e.team] !== false && !disabledCats.has(e.type) && (!q || String(e.jersey).includes(q))
    );
  }, [allEvents, teams, disabledCats, jerseyQuery]);

  // One lane per present category; markers coloured by category, bordered by team.
  const editorData = useMemo(() => {
    let count = 0;
    return presentCats
      .filter((c) => !disabledCats.has(c))
      .map((type) => {
        const actions = [];
        for (const e of filtered) {
          if (e.type !== type) continue;
          if (count >= MAX_ACTIONS) break;
          actions.push(e);
          count += 1;
        }
        return { id: type, actions };
      });
  }, [presentCats, filtered, disabledCats]);

  const visibleRows = editorData.length;
  const effects = useMemo(() => ({ "event-marker": { id: "event-marker", name: "Event" } }), []);

  const MIN_SCALE_WIDTH = 30;
  const rawScaleWidth = Math.max(containerWidth, 500) / Math.max(durationSec, 1);
  const scaleW = Math.max(rawScaleWidth, MIN_SCALE_WIDTH) * zoomLevel;
  const visibleSpanSec = scaleW > 0 ? containerWidth / scaleW : durationSec;

  // ── resize / width listeners ──
  useEffect(() => {
    const onMove = (e) => {
      if (!resizingRef.current || !containerRef.current) return;
      const h = e.clientY - containerRef.current.getBoundingClientRect().top;
      if (h > 120 && h < 600) setTimelineHeight(h);
    };
    const onUp = () => { resizingRef.current = false; };
    const onResize = () => { if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth - GUTTER_W); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("resize", onResize);
    requestAnimationFrame(onResize);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // ── engine listeners (kept for VideoTab play/pause bridge) ──
  useEffect(() => {
    if (!timelineState.current) return;
    const engine = timelineState.current;
    engine.listener.on("play", () => setIsPlaying(true));
    engine.listener.on("paused", () => setIsPlaying(false));
    engine.listener.on("afterSetTime", ({ time }) => setTime(time * 1000));
    engine.listener.on("setTimeByTick", ({ time }) => {
      setTime(time * 1000);
      if (autoScrollWhenPlay.current && timelineState.current) {
        const left = time * scaleW - containerWidth / 2;
        timelineState.current.setScrollLeft(Math.max(0, left));
      }
    });
    return () => { engine.pause(); engine.listener.offAll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZoomIn = () => setZoomLevel((p) => Math.min(p + 0.5, 12));
  const handleZoomOut = () => setZoomLevel((p) => Math.max(p - 0.5, 0.5));

  const toggleTeam = (t) => setTeams((p) => ({ ...p, [t]: !p[t] }));
  const toggleCat = (c) =>
    setDisabledCats((prev) => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; });

  const sortedFiltered = useMemo(() => [...filtered].sort((a, b) => a.start - b.start), [filtered]);
  const gotoAdjacentEvent = (dir) => {
    if (!sortedFiltered.length) return;
    const t = videoTimeSec;
    let target;
    if (dir > 0) target = sortedFiltered.find((e) => e.start > t + 0.05);
    else target = [...sortedFiltered].reverse().find((e) => e.start < t - 0.05);
    if (target) setTime(target.start * 1000);
  };

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", position: "relative", marginBottom: 50, userSelect: resizingRef.current ? "none" : "auto" }}
    >
      {/* ── Toolbar: filters + controls ── */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        {showTimeline && (
          <Button size="small" variant="outlined" onClick={() => setShowTimeline(false)}
            sx={{ textTransform: "none", fontSize: 11, borderRadius: "6px", borderColor: "var(--border-1)", color: "var(--text-2)" }}>
            Hide timeline
          </Button>
        )}

        {/* Team toggles */}
        {["A", "B"].map((tm) => (
          <Chip key={tm} size="small" label={`Team ${tm}`} onClick={() => toggleTeam(tm)}
            sx={{
              height: 24, fontSize: 11, fontWeight: 700, cursor: "pointer",
              backgroundColor: teams[tm] ? TEAM_COLOR[tm] : "var(--surface-2)",
              color: teams[tm] ? "#fff" : "var(--text-2)",
              border: `1px solid ${TEAM_COLOR[tm]}`,
            }} />
        ))}

        <TextField size="small" placeholder="Jersey #" value={jerseyQuery} onChange={(e) => setJerseyQuery(e.target.value)}
          InputProps={{ startAdornment: <Search sx={{ fontSize: 14, color: "var(--text-2)", mr: 0.5 }} />, sx: { height: 28, fontSize: 11, width: 110, borderRadius: "6px", backgroundColor: "var(--surface-2)" } }} />

        <Tooltip title="Previous event"><span><IconButton size="small" onClick={() => gotoAdjacentEvent(-1)} sx={{ border: "1px solid var(--border-1)", borderRadius: "6px", width: 28, height: 28 }}><ChevronLeft sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
        <Tooltip title="Next event"><span><IconButton size="small" onClick={() => gotoAdjacentEvent(1)} sx={{ border: "1px solid var(--border-1)", borderRadius: "6px", width: 28, height: 28 }}><ChevronRight sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>

        <Box flex={1} />

        <Typography sx={{ fontSize: 11, color: "var(--text-2)" }}>{filtered.length} events</Typography>
        <Tooltip title="Zoom out"><IconButton size="small" onClick={handleZoomOut} sx={{ border: "1px solid var(--border-1)", borderRadius: "6px", width: 28, height: 28 }}><Remove sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Zoom in"><IconButton size="small" onClick={handleZoomIn} sx={{ border: "1px solid var(--border-1)", borderRadius: "6px", width: 28, height: 28 }}><Add sx={{ fontSize: 16 }} /></IconButton></Tooltip>
      </Stack>

      {/* ── Category legend / filter chips ── */}
      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
        {presentCats.map((c) => {
          const on = !disabledCats.has(c);
          return (
            <Chip key={c} size="small" label={ATTRIBUTE_LABEL[c] || c} onClick={() => toggleCat(c)}
              sx={{
                height: 22, fontSize: 10, cursor: "pointer",
                backgroundColor: on ? `${ATTRIBUTE_COLOR[c]}22` : "transparent",
                color: on ? "var(--text-1)" : "var(--text-2)",
                border: `1px solid ${ATTRIBUTE_COLOR[c]}`,
                opacity: on ? 1 : 0.5,
                "& .MuiChip-label": { px: 0.75 },
              }} />
          );
        })}
      </Stack>

      {/* ── Overview density bar ── */}
      <div style={{ marginBottom: 8 }}>
        <OverviewBar
          events={filtered}
          durationSec={durationSec}
          playheadSec={videoTimeSec}
          viewStart={Math.max(0, videoTimeSec - visibleSpanSec / 2)}
          viewEnd={Math.min(durationSec, videoTimeSec + visibleSpanSec / 2)}
          onSeek={(sec) => setTime(sec * 1000)}
        />
      </div>

      {/* ── Detail lanes (gutter + zoomable timeline) ── */}
      {!isNaN(durationSec) && durationSec > 0 && (
        <div style={{ position: "relative", display: "flex", height: timelineHeight, border: "1px solid var(--border-1)", borderRadius: 6, overflow: "hidden" }}>
          {/* left gutter with category labels */}
          <div style={{ width: GUTTER_W, flexShrink: 0, background: "var(--surface-1)", borderRight: "1px solid var(--border-1)" }}>
            <div style={{ height: SCALE_HEIGHT, borderBottom: "1px solid var(--border-1)" }} />
            {editorData.map((row) => (
              <div key={row.id} style={{ height: ROW_HEIGHT, display: "flex", alignItems: "center", gap: 6, padding: "0 8px", borderBottom: "1px solid var(--border-1)" }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: ATTRIBUTE_COLOR[row.id], flexShrink: 0 }} />
                <Typography sx={{ fontSize: 10, color: "var(--text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {ATTRIBUTE_LABEL[row.id] || row.id}
                </Typography>
              </div>
            ))}
          </div>

          {/* timeline */}
          <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            <Timeline
              key={`tl-${containerWidth}-${visibleRows}`}
              style={{ width: `${containerWidth}px`, height: "100%" }}
              rowHeight={ROW_HEIGHT}
              scaleSplitCount={10}
              onChange={() => {}}
              disableDrag
              scale={1}
              minScaleCount={durationSec}
              maxScaleCount={durationSec}
              scaleWidth={scaleW}
              startLeft={20}
              autoScroll
              ref={timelineState}
              editorData={editorData}
              effects={effects}
              onClickTimeArea={(t) => { setTime(Math.min(t, durationSec) * 1000); return true; }}
              onCursorDrag={(t) => setTime(Math.min(t, durationSec) * 1000)}
              getActionRender={(action) => <CustomActionRender action={action} />}
              getScaleRender={(scaleIndex) => (
                <Typography variant="caption" component="span" sx={{ fontSize: 10, color: "var(--text-2)" }}>
                  {fmtTime(scaleIndex)}
                </Typography>
              )}
            />
          </div>
        </div>
      )}

      {filtered.length >= MAX_ACTIONS && (
        <Typography sx={{ fontSize: 10, color: "var(--text-2)", mt: 0.5 }}>
          Showing first {MAX_ACTIONS} events — narrow filters to see more.
        </Typography>
      )}

      {/* resize handle */}
      <div
        onMouseDown={() => { resizingRef.current = true; }}
        style={{ height: 6, width: "100%", cursor: "ns-resize", background: "rgba(0,0,0,0.08)", borderRadius: 3, marginTop: 4 }}
      />
    </div>
  );
};

export default VideoTimelineEditor;
